import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter, Input, Table } from "reactstrap";
import { Download } from "lucide-react";
import Select from "react-select";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import moment from "moment";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getPayrolls } from "../../components/Api/GmsPayroll";
import { getPayrollDetailHistory, exportPayrollDetailsHistoryToExcel } from "../../components/Api/GmsPayrollDetailHistory";
import { getPayrollOtherDetailsHistory } from "../../components/Api/GmsPayrollOtherDetailsHistory";
import axios from "axios";
import BASE_URL from "../../config";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

const PayrollDetailsHistoryReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [payrollDetails, setPayrollDetails] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [additionalComponents, setAdditionalComponents] = useState([]);

  // Filter states
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedViewEmployee, setSelectedViewEmployee] = useState(null);
  const [selectedPayslipEmployee, setSelectedPayslipEmployee] = useState(null);
  const [payrollOtherDetails, setPayrollOtherDetails] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPayrollDetailsHistoryReport');
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (!hasPermission) {
      Swal.fire({
        icon: 'warning',
        title: 'Access Denied',
        text: 'You do not have permission to access this page.',
        showConfirmButton: false,
        timer: 3000
      }).then(() => {
        window.history.back();
      });
      return;
    }

    loadInitialData();
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
        // Load branches for the auto-populated business and auto-populate branch if user has branchId
        loadBranches(session.businessId, true);
      }
    }
  }, [businesses, session.businessId]);

  // Note: Branch auto-population is now handled inside loadBranches function
  // This ensures it happens immediately after branches are loaded

  // Auto-load employees when business is auto-populated
  useEffect(() => {
    if (session.businessId && selectedBusiness?.value === session.businessId && businesses.length > 0) {
      loadEmployees(selectedBusiness.value, selectedBranch?.value);
    }
  }, [selectedBusiness, selectedBranch, session.businessId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBusinesses(),
        loadDesignations(),
        loadInitialPayrollData()
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({});
      const data = Array.isArray(response) ? response : (response.data || []);
      setBusinesses(data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_Name
      })));
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadDesignations = async () => {
    try {
      console.log('[PayrollDetailsHistory] Loading designations...');
      const response = await axios.post(`${BASE_URL}/api/Designation/get`, {}, {
        headers: getAuthHeader(),
      });
      console.log('[PayrollDetailsHistory] Designations API response:', response.data);
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log('[PayrollDetailsHistory] Processed designations:', data);
      console.log('[PayrollDetailsHistory] Designations count:', data.length);
      setDesignations(data);
    } catch (error) {
      console.error("Error loading designations:", error);
    }
  };

  const loadAdditionalComponents = async (businessId = null) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/AdditionalComponent/get`, {
        Component_Business_Id: businessId
      }, { headers: getAuthHeader() });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setAdditionalComponents(data);
    } catch (error) {
      console.error("Error loading additional components:", error);
    }
  };

  const loadInitialPayrollData = async () => {
    try {
      // Get only posted payrolls
      const payrollResponse = await getPayrolls({ is_posted: true });
      const payrollData = Array.isArray(payrollResponse) ? payrollResponse : (payrollResponse.data || []);

      if (payrollData.length > 0) {
        // Filter and sort payrolls
        const postedPayrolls = payrollData
          .filter(p => p.payroll_IsPosted)
          .sort((a, b) => new Date(b.payroll_Date) - new Date(a.payroll_Date))
          .map(p => ({
            value: p.payroll_Id,
            label: moment(p.payroll_Date).format('MMMM YYYY'),
            date: p.payroll_Date
          }));

        setPayrolls(postedPayrolls);

        // Get the latest posted payroll
        const latestPayroll = postedPayrolls[0];
        setSelectedPayroll(latestPayroll);

        // Load payroll details for the latest month
        if (latestPayroll) {
          loadPayrollDetailsData(latestPayroll.value, {});
        }
      }
    } catch (error) {
      console.error("Error loading initial payroll data:", error);
    }
  };

  const loadPayrollDetailsData = async (payrollId, filters = {}) => {
    if (!payrollId) return;

    setLoading(true);
    try {
      // Get payroll date first - fetch the payroll record to get the date
      let payrollDate = null;
      try {
        console.log('[PayrollDetailsHistory] Fetching payroll date for Payroll_Id:', payrollId);
        const payrollResponse = await getPayrolls({ payroll_Id: payrollId });
        const payrollData = Array.isArray(payrollResponse) ? payrollResponse : (payrollResponse.data || []);
        if (payrollData.length > 0 && payrollData[0].payroll_Date) {
          payrollDate = moment(payrollData[0].payroll_Date).format('MMMM YYYY');
          console.log('[PayrollDetailsHistory] Payroll date fetched:', payrollDate, 'from Payroll_Date:', payrollData[0].payroll_Date);
        }
      } catch (error) {
        console.error("Error fetching payroll date:", error);
      }

      // Get payroll details
      const detailsResponse = await getPayrollDetailHistory({
        payroll_Id: payrollId,
        employee_Id: filters.employee_Id || null
      });
      const details = Array.isArray(detailsResponse) ? detailsResponse : (detailsResponse.data || []);

      if (details.length === 0) {
        setPayrollDetails([]);
        setFilteredData([]);
        setLoading(false);
        return;
      }

      // Get employee data if filter is applied
      let employeeData = null;
      if (filters.employee_Id) {
        const employeeResponse = await getEmployees({ employee_Id: filters.employee_Id });
        employeeData = Array.isArray(employeeResponse) ? employeeResponse[0] : employeeResponse.data?.[0];
      }

      // Get additional components for the business
      const componentsResponse = await axios.post(`${BASE_URL}/api/AdditionalComponent/get`, {
        Component_Business_Id: filters.businessId || null
      }, { headers: getAuthHeader() });
      const components = Array.isArray(componentsResponse.data) ? componentsResponse.data : [];

      // Get other details
      const otherDetailsResponse = await getPayrollOtherDetailsHistory({
        payroll_Id: payrollId,
        employee_Id: filters.employee_Id || null
      });
      const otherDetails = Array.isArray(otherDetailsResponse) ? otherDetailsResponse : (otherDetailsResponse.data || []);

      console.log('[PayrollDetailsHistory] Final payroll date to use:', payrollDate);
      console.log('[PayrollDetailsHistory] Designations loaded:', designations.length);

      // Ensure designations are loaded - reload if empty
      let designationsToUse = designations;
      if (designations.length === 0) {
        console.log('[PayrollDetailsHistory] Designations empty, reloading...');
        try {
          const designationResponse = await axios.post(`${BASE_URL}/api/Designation/get`, {}, {
            headers: getAuthHeader(),
          });
          const designationData = Array.isArray(designationResponse.data) ? designationResponse.data : (designationResponse.data?.data || []);
          designationsToUse = designationData;
          setDesignations(designationData);
          console.log('[PayrollDetailsHistory] Designations reloaded:', designationData.length);
        } catch (error) {
          console.error("Error reloading designations:", error);
        }
      }

      // Combine the data
      const enrichedData = await Promise.all(details.map(async (detail) => {
        // If no specific employee filter, get employee data for each record
        let empData = employeeData;
        if (!filters.employee_Id) {
          const empResponse = await getEmployees({ employee_Id: detail.employee_Id });
          empData = Array.isArray(empResponse) ? empResponse[0] : empResponse.data?.[0];
        }

        // Get designation name - use the designations we have
        let designationName = '';
        if (designationsToUse.length > 0 && empData?.employee_Designation_ID) {
          const designation = designationsToUse.find(d => d.designation_Id === empData.employee_Designation_ID);
          designationName = designation ? (designation.designation_NameEn || designation.designation_Name || '') : '';
          if (!designationName && empData.employee_Designation_ID) {
            console.log(`[PayrollDetailsHistory] Designation not found for ID: ${empData.employee_Designation_ID}, Employee: ${empData.employee_NameEn}`);
            console.log(`[PayrollDetailsHistory] Available designation IDs:`, designationsToUse.map(d => d.designation_Id));
          }
        } else if (empData?.employee_Designation_ID) {
          console.log(`[PayrollDetailsHistory] Designations not available. Employee Designation_ID: ${empData.employee_Designation_ID}, Employee: ${empData.employee_NameEn}`);
          console.log(`[PayrollDetailsHistory] Designations array length: ${designationsToUse.length}`);
        }

        return {
          ...detail,
          employee_Name: empData?.employee_NameEn || empData?.employee_Name || 'Unknown',
          employee_Code: empData?.employee_Code || 'N/A',
          designation_Name: designationName,
          payrollMonth: payrollDate || 'N/A', // Use the fetched payroll date
          otherDetails: otherDetails.filter(od => od.employee_Id === detail.employee_Id),
          components: components
        };
      }));

      setPayrollDetails(enrichedData);
      setFilteredData(enrichedData);
      setAdditionalComponents(components);
      setPayrollOtherDetails(otherDetails);
    } catch (error) {
      console.error("Error loading payroll details:", error);
      Swal.fire("Error", "Failed to load payroll details", "error");
      setPayrollDetails([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async (businessId, shouldAutoPopulateBranch = false) => {
    try {
      const response = await getBranches({ Business_Id: businessId });
      const data = Array.isArray(response) ? response : (response.data || []);
      const branchOptions = data.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_Name
      }));
      setBranches(branchOptions);

      // Auto-populate branch if user has branchId and it should be auto-populated
      if (shouldAutoPopulateBranch && session.branchId && !selectedBranch) {
        const branch = branchOptions.find(b => b.value === session.branchId);
        if (branch) {
          setSelectedBranch(branch);
          // Load departments for the auto-populated branch
          loadDepartments(branch.value);
          loadEmployees(businessId, branch.value);
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadDepartments = async (branchId) => {
    try {
      const response = await getDepartments({ Branch_Id: branchId });
      const data = Array.isArray(response) ? response : (response.data || []);
      setDepartments(data.map(d => ({
        value: d.department_Id,
        label: d.department_NameEn || d.department_Name
      })));
    } catch (error) {
      console.error("Error loading departments:", error);
    }
  };

  const loadEmployees = async (businessId, branchId = null, departmentId = null) => {
    try {
      const filter = {};
      // Use provided businessId or session businessId
      const finalBusinessId = businessId || session.businessId;
      if (finalBusinessId) {
        filter.employee_Business_Id = finalBusinessId;
      }
      // Use provided branchId or session branchId
      const finalBranchId = branchId || (session.branchId && session.businessId ? session.branchId : null);
      if (finalBranchId) {
        filter.employee_Branch_Id = finalBranchId;
      }
      if (departmentId) {
        filter.employee_Department_ID = departmentId;
      }

      const response = await getEmployees(filter);
      const data = Array.isArray(response) ? response : (response.data || []);
      setEmployees(data.map(e => ({
        value: e.employee_Id,
        label: `${e.employee_Code} - ${e.employee_NameEn || e.employee_Name}`
      })));
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const handleApplyFilter = () => {
    if (!selectedPayroll) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Field',
        text: 'Please select Payroll Month',
        showConfirmButton: true
      });
      return;
    }

    const filters = {};
    if (selectedBusiness) filters.businessId = selectedBusiness.value;
    if (selectedEmployee) filters.employee_Id = selectedEmployee.value;
    if (selectedBranch) filters.branchId = selectedBranch.value;
    if (selectedDepartment) filters.departmentId = selectedDepartment.value;

    loadPayrollDetailsData(selectedPayroll.value, filters);
  };

  // Manual search function for employee search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedBusiness(null);
    setSelectedBranch(null);
    setSelectedDepartment(null);
    setSelectedEmployee(null);
    setSelectedPayroll(null);
    setBranches([]);
    setDepartments([]);
    setEmployees([]);
    setEmployeeSearchOptions([]);
    setPayrollDetails([]);
    setFilteredData([]);
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    if (!selectedPayroll) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Field',
        text: 'Please select Payroll Month',
        showConfirmButton: true
      });
      return;
    }

    try {
      setLoading(true);
      const filterData = {
        payroll_Id: selectedPayroll.value,
        business_Id: selectedBusiness?.value || null,
        branch_Id: selectedBranch?.value || null,
        department_Id: selectedDepartment?.value || null,
        employee_Id: selectedEmployee?.value || null
      };

      await exportPayrollDetailsHistoryToExcel(filterData);
      Swal.fire("Success", "Payroll details history report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export payroll details history report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (employee) => {
    try {
      setLoading(true);

      // Load payroll other details for this employee
      const otherDetailsResponse = await getPayrollOtherDetailsHistory({
        Payroll_Id: selectedPayroll.value,
        Employee_Id: employee.employee_Id
      });

      setPayrollOtherDetails(Array.isArray(otherDetailsResponse) ? otherDetailsResponse : (otherDetailsResponse.data || []));
      setSelectedViewEmployee(employee);
      setShowViewModal(true);
    } catch (error) {
      console.error("Error loading employee details:", error);
      Swal.fire("Error", "Failed to load employee details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPayslip = async (employee) => {
    try {
      setLoading(true);

      // Load payroll other details for this employee
      const otherDetailsResponse = await getPayrollOtherDetailsHistory({
        Payroll_Id: selectedPayroll.value,
        Employee_Id: employee.employee_Id
      });

      setPayrollOtherDetails(Array.isArray(otherDetailsResponse) ? otherDetailsResponse : (otherDetailsResponse.data || []));
      setSelectedPayslipEmployee(employee);
      setShowPayslipModal(true);
    } catch (error) {
      console.error("Error loading payslip data:", error);
      Swal.fire("Error", "Failed to load payslip data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View Modal Component (Read-only version of PayrollManageModal)
  const ViewModal = ({ isOpen, toggle, employee, additionalComponents, payrollOtherDetails }) => {
    const [showAdditions, setShowAdditions] = useState(true);
    const [showDeductions, setShowDeductions] = useState(true);
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [totalAdditions, setTotalAdditions] = useState(0);
    const [totalDeductions, setTotalDeductions] = useState(0);
    const [netSalary, setNetSalary] = useState(0);
    const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);

    const additionRef = useRef(null);
    const deductionRef = useRef(null);

    useEffect(() => {
      if (employee && payrollOtherDetails?.length > 0) {
        // Map the existing details to our component format
        const existingComponents = payrollOtherDetails.map(pod => {
          const matchingComponent = additionalComponents.find(ac => ac.component_Id === pod.component_Id);
          if (!matchingComponent) return null;

          return {
            Component_Id: pod.component_Id,
            Component_Name: matchingComponent.component_NameEn || matchingComponent.component_Name,
            Is_Deduction: matchingComponent.component_IsDeduction,
            Amount: pod.amount || 0,
            Notes: pod.notes || ''
          };
        }).filter(comp => comp !== null);

        setSelectedComponents(existingComponents);
        calculateTotals(existingComponents, employee.salaryAdvance_Deduction || 0);
      } else {
        setSelectedComponents([]);
        calculateTotals([], employee?.salaryAdvance_Deduction || 0);
      }

      setSalaryAdvanceBalance(employee?.salaryAdvance_Amount || 0);
    }, [employee, additionalComponents, payrollOtherDetails]);

    const calculateTotals = (components, advanceDeduction) => {
      const additions = components
        .filter(d => !d.Is_Deduction)
        .reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);

      const deductions = components
        .filter(d => d.Is_Deduction)
        .reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);

      setTotalAdditions(additions);
      setTotalDeductions(deductions);

      const newNetSalary = (employee?.gross_Salary || 0)
        - (employee?.absent_Deduction || 0)
        - (parseFloat(advanceDeduction) || 0)
        + additions
        - deductions;

      setNetSalary(newNetSalary);
    };

    if (!employee) return null;

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          View Payroll Details - {employee?.employee_Name}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Basic Salary</Label>
                  <Input value={(employee?.basic_Salary || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>HRA</Label>
                  <Input value={(employee?.hra || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Transport Allowance</Label>
                  <Input value={(employee?.transport_Allowance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Other Allowance</Label>
                  <Input value={(employee?.other_Allowance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Food Allowance</Label>
                  <Input value={(employee?.food_Allowance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Communication Allowance</Label>
                  <Input value={(employee?.communication_Allowance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Utility Allowance</Label>
                  <Input value={(employee?.utility_Allowance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>WPS Salary</Label>
                  <Input value={(employee?.wpS_Salary || employee?.wpsSalary || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Performance Incentive</Label>
                  <Input value={(employee?.performance_Incentive || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Sales Incentive</Label>
                  <Input value={(employee?.sales_Incentive || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Gross Salary</Label>
                  <Input value={(employee?.gross_Salary || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Absent Days</Label>
                  <Input value={employee?.absent_Days || 0} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Absent Deduction</Label>
                  <Input
                    value={(employee?.absent_Deduction || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    disabled
                    className="text-danger"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Salary Advance Balance</Label>
                  <Input
                    value={salaryAdvanceBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    disabled
                    className="text-danger font-weight-bold"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Salary Advance Deduction</Label>
                  <Input
                    value={(employee?.salaryAdvance_Deduction || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    className="text-danger"
                    disabled
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-4">
              <h5>Additional Components</h5>
              <div className="d-flex justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <Button
                    color="success"
                    onClick={() => setShowAdditions(!showAdditions)}
                    className="me-2"
                  >
                    Variable Pay
                  </Button>
                  <span className="text-success ms-2">
                    Total: {totalAdditions.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <span className="text-danger me-2">
                    Total: {totalDeductions.toFixed(2)}
                  </span>
                  <Button
                    color="danger"
                    onClick={() => setShowDeductions(!showDeductions)}
                  >
                    Deductions
                  </Button>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  {showAdditions && (
                    <div className="border p-3 mb-3" ref={additionRef}>
                      <h6 className="text-success">Variable Pay Details</h6>
                      {selectedComponents.filter(comp => !comp.Is_Deduction).length > 0 ? (
                        selectedComponents
                          .filter(comp => !comp.Is_Deduction)
                          .map((comp) => (
                            <Row key={comp.Component_Id} className="mb-2">
                              <Col md={4}>
                                <FormGroup>
                                  <Label>{comp.Component_Name}</Label>
                                  <Input
                                    value={(comp.Amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    className="text-success"
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={8}>
                                <FormGroup>
                                  <Label>Notes</Label>
                                  <Input
                                    value={comp.Notes || ''}
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          ))
                      ) : (
                        <p className="text-muted">No variable pay components</p>
                      )}
                    </div>
                  )}

                  {showDeductions && (
                    <div className="border p-3" ref={deductionRef}>
                      <h6 className="text-danger">Deduction Details</h6>
                      {selectedComponents.filter(comp => comp.Is_Deduction).length > 0 ? (
                        selectedComponents
                          .filter(comp => comp.Is_Deduction)
                          .map((comp) => (
                            <Row key={comp.Component_Id} className="mb-2">
                              <Col md={4}>
                                <FormGroup>
                                  <Label>{comp.Component_Name}</Label>
                                  <Input
                                    value={(comp.Amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    className="text-danger"
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={8}>
                                <FormGroup>
                                  <Label>Notes</Label>
                                  <Input
                                    value={comp.Notes || ''}
                                    disabled
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          ))
                      ) : (
                        <p className="text-muted">No deduction components</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <div>
                <strong>Net Salary: </strong>
                <span className="h5 mb-0">{netSalary.toFixed(2)}</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <strong>By WPS:</strong> {(() => {
                    const wpsSalary = employee?.wpS_Salary || employee?.wpsSalary || 0;
                    const byWps = netSalary <= wpsSalary ? netSalary : wpsSalary;
                    return byWps.toFixed(2);
                  })()} |
                  <strong> In Hand:</strong> {(() => {
                    const wpsSalary = employee?.wpS_Salary || employee?.wpsSalary || 0;
                    const inHand = netSalary > wpsSalary ? (netSalary - wpsSalary) : 0;
                    return inHand.toFixed(2);
                  })()}
                </small>
              </div>
            </div>
            <div>
              <Button color="secondary" onClick={toggle}>Close</Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  // Payslip Modal Component
  const PayslipModal = ({ isOpen, toggle, employee }) => {
    if (!employee) return null;

    const formatAmount = (amount) => {
      return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Get additional components from payrollOtherDetails
    const additionalEarnings = payrollOtherDetails
      .filter(detail => !detail.is_Deduction && detail.amount > 0)
      .map(detail => {
        const component = additionalComponents.find(c => c.component_Id === detail.component_Id);
        return {
          description: component?.component_NameEn || 'Additional Component',
          amount: detail.amount
        };
      });

    const additionalDeductions = payrollOtherDetails
      .filter(detail => detail.is_Deduction && detail.amount > 0)
      .map(detail => {
        const component = additionalComponents.find(c => c.component_Id === detail.component_Id);
        return {
          description: component?.component_NameEn || 'Additional Deduction',
          amount: detail.amount
        };
      });

    // Base earnings that have values > 0
    const earnings = [
      { description: "Basic Salary", amount: employee.basic_Salary },
      { description: "HRA", amount: employee.hra },
      { description: "Transport Allowance", amount: employee.transport_Allowance },
      { description: "Other Allowance", amount: employee.other_Allowance },
      { description: "Food Allowance", amount: employee.food_Allowance },
      { description: "Communication Allowance", amount: employee.communication_Allowance },
      { description: "Utility Allowance", amount: employee.utility_Allowance },
      { description: "Performance Incentive", amount: employee.performance_Incentive },
      { description: "Sales Incentive", amount: employee.sales_Incentive },
      ...additionalEarnings
    ].filter(item => parseFloat(item.amount) > 0);

    // Base deductions that have values > 0
    const deductions = [
      ...(employee.absent_Days > 0 ? [{
        description: `Absent Deduction (${employee.absent_Days} day${employee.absent_Days === 1 ? '' : 's'})`,
        amount: employee.absent_Deduction
      }] : []),
      ...(employee.salaryAdvance_Deduction > 0 ? [{
        description: "Salary Advance Deduction",
        amount: employee.salaryAdvance_Deduction
      }] : []),
      ...additionalDeductions
    ].filter(item => parseFloat(item.amount) > 0);

    const earningsTotal = earnings.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const deductionsTotal = deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const netPay = earningsTotal - deductionsTotal;

    const handlePrint = () => {
      const now = new Date();
      const formattedDate = now.toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
      const fileName = `Payslip_${employee.employee_NameEn?.replace(/\s+/g, '') || employee.employee_Name?.replace(/\s+/g, '')}_${employee.employee_Code}_${formattedDate}`;

      const doc = new jsPDF();

      // Set font styles
      doc.setFont("helvetica");

      // Header
      doc.setFontSize(16);
      doc.text(`${(selectedBusiness?.label || 'COMPANY').toUpperCase()}`, doc.internal.pageSize.width / 2, 20, { align: "center" });
      doc.text('PAYSLIP', doc.internal.pageSize.width / 2, 30, { align: "center" });

      doc.setFontSize(11);
      const payrollMonth = selectedPayroll?.label?.split(' - ')[0] || 'N/A';
      doc.text(payrollMonth, doc.internal.pageSize.width / 2, 40, { align: "center" });

      // Employee Info
      doc.setFontSize(10);
      doc.text(`Employee Name: ${employee.employee_NameEn || employee.employee_Name}`, 20, 55);
      doc.text(`Employee Code: ${employee.employee_Code}`, doc.internal.pageSize.width - 60, 55);

      // Table Headers
      let yPos = 70;
      const xStart = 20;
      const colWidths = [15, 100, 40];

      // Helper function for table rows
      const addTableRow = (cols, isHeader = false, isBold = false) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        if (isBold) doc.setFont("helvetica", "bold");
        else doc.setFont("helvetica", "normal");

        let xPos = xStart;
        cols.forEach((text, i) => {
          if (i === 2) {
            doc.text(text.toString(), xPos + colWidths[i], yPos, { align: "right" });
          } else {
            doc.text(text.toString(), xPos, yPos);
          }
          xPos += colWidths[i];
        });

        if (isHeader) {
          doc.line(xStart, yPos + 2, xStart + colWidths.reduce((a, b) => a + b), yPos + 2);
        }

        yPos += 7;
      };

      // Headers
      addTableRow(["S.NO", "DESCRIPTION", "AMOUNT"], true, true);

      // Earnings Section
      yPos += 3;
      addTableRow(["", "EARNINGS", ""], false, true);
      earnings.forEach((item, index) => {
        addTableRow([
          (index + 1).toString(),
          item.description,
          formatAmount(item.amount)
        ]);
      });

      // Earnings Total
      yPos += 2;
      addTableRow(["", "Sub Total", formatAmount(earningsTotal)], false, true);

      // Deductions Section
      yPos += 5;
      addTableRow(["", "DEDUCTIONS", ""], false, true);
      deductions.forEach((item, index) => {
        addTableRow([
          (index + 1).toString(),
          item.description,
          formatAmount(item.amount)
        ]);
      });

      // Deductions Total
      yPos += 2;
      addTableRow(["", "Sub Total", formatAmount(deductionsTotal)], false, true);

      // Net Pay
      yPos += 5;
      addTableRow(["", "NET PAY", formatAmount(netPay)], false, true);

      // WPS and In Hand breakdown
      yPos += 2;
      const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
      const byWps = netPay <= wpsSalary ? netPay : wpsSalary;
      const inHand = netPay > wpsSalary ? (netPay - wpsSalary) : 0;

      addTableRow(["1", "By WPS", formatAmount(byWps)]);
      addTableRow(["2", "In Hand", formatAmount(inHand)]);

      // Signatures
      yPos += 20;
      if (yPos > 250) {
        doc.addPage();
        yPos = 30;
      }
      doc.text("Prepared By: Jijo Thomas", 20, yPos);
      doc.text("Received By: _________________", doc.internal.pageSize.width - 70, yPos);

      // Save PDF
      doc.save(`${fileName}.pdf`);
    };

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Payslip - {employee.employee_NameEn || employee.employee_Name}
        </ModalHeader>
        <ModalBody>
          <div className="border p-4">
            <div className="text-center mb-4">
              <h4>{(selectedBusiness?.label || 'COMPANY').toUpperCase()}</h4>
              <h4>PAYSLIP</h4>
              <p className="mb-1">{selectedPayroll?.label?.split(' - ')[0] || 'N/A'}</p>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>Employee Name:</strong> {employee.employee_NameEn || employee.employee_Name}</td>
                      <td><strong>Employee Code:</strong> {employee.employee_Code}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>DESCRIPTION</th>
                      <th className="text-end">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="3"><strong>EARNINGS</strong></td>
                    </tr>
                    {earnings.map((item, index) => (
                      <tr key={`earning-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.description}</td>
                        <td className="text-end">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="2" className="text-end"><strong>Sub Total</strong></td>
                      <td className="text-end"><strong>{formatAmount(earningsTotal)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="3"><strong>DEDUCTIONS</strong></td>
                    </tr>
                    {deductions.map((item, index) => (
                      <tr key={`deduction-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.description}</td>
                        <td className="text-end">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="2" className="text-end"><strong>Sub Total</strong></td>
                      <td className="text-end"><strong>{formatAmount(deductionsTotal)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="2"><strong>NET PAY</strong></td>
                      <td className="text-end"><strong>{formatAmount(netPay)}</strong></td>
                    </tr>
                    <tr>
                      <td>1</td>
                      <td>By WPS</td>
                      <td className="text-end">{(() => {
                        const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
                        const byWps = netPay <= wpsSalary ? netPay : wpsSalary;
                        return formatAmount(byWps);
                      })()}</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>In Hand</td>
                      <td className="text-end">{(() => {
                        const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
                        const inHand = netPay > wpsSalary ? (netPay - wpsSalary) : 0;
                        return formatAmount(inHand);
                      })()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-6">
                <p><strong>Prepared By:</strong> Jijo Thomas</p>
              </div>
              <div className="col-6 text-end">
                <p><strong>Received By:</strong> _________________</p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrint}>Print</Button>
          <Button color="secondary" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center">
            <h4 className="card-title mb-0 flex-grow-1">Payroll Details History Report</h4>
            <Button
              color="success"
              onClick={handleExportExcel}
              disabled={!selectedPayroll || loading}
              className="ms-2"
            >
              <Download className="me-1" size={16} />
              Export Excel
            </Button>
          </CardHeader>
          <CardBody>
            <Form>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Company <span className="text-danger">*</span></Label>
                    <Select
                      value={selectedBusiness}
                      onChange={(selected) => {
                        setSelectedBusiness(selected);
                        setBranches([]);
                        setDepartments([]);
                        setEmployees([]);
                        setSelectedEmployee(null);
                        setEmployeeSearchOptions([]);
                        if (selected) {
                          loadBranches(selected.value);
                          loadAdditionalComponents(selected.value);
                          loadEmployees(selected.value);
                        }
                      }}
                      options={businesses}
                      isClearable={!session.businessId}
                      isDisabled={!!session.businessId}
                      placeholder="Select Company"
                      className={!selectedBusiness ? "is-invalid" : ""}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Select
                      value={selectedBranch}
                      onChange={(selected) => {
                        setSelectedBranch(selected);
                        setDepartments([]);
                        setSelectedEmployee(null);
                        setEmployeeSearchOptions([]);
                        if (selected && selectedBusiness) {
                          loadDepartments(selected.value);
                          loadEmployees(selectedBusiness.value, selected.value);
                        }
                      }}
                      options={branches}
                      isClearable={!session.branchId}
                      isDisabled={!!session.branchId || !selectedBusiness}
                      placeholder="Select Branch"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Department</Label>
                    <Select
                      value={selectedDepartment}
                      onChange={(selected) => {
                        setSelectedDepartment(selected);
                        if (selected && selectedBusiness) {
                          loadEmployees(selectedBusiness.value, selectedBranch?.value, selected.value);
                        }
                      }}
                      options={departments}
                      isClearable
                      placeholder="Select Department"
                      isDisabled={!selectedBranch}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Employee</Label>
                    <Select
                      value={selectedEmployee?.value ? employeeSearchOptions.find(opt => opt.value === selectedEmployee.value) || selectedEmployee : null}
                      onChange={(selected) => {
                        setSelectedEmployee(selected);
                        if (selected) {
                          setEmployeeSearchOptions([]);
                        }
                      }}
                      options={employeeSearchOptions}
                      isClearable
                      isLoading={employeeSearchLoading}
                      filterOption={() => true}
                      noOptionsMessage={({ inputValue }) => {
                        if (employeeSearchLoading) return "Searching...";
                        if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                        return "No employees found";
                      }}
                      loadingMessage={() => "Searching employees..."}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change' && selectedBusiness) {
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchEmployeesManually(inputValue);
                          }, 300);
                          setSearchTimeout(timeout);
                        }
                        return inputValue;
                      }}
                      placeholder="Type at least 3 characters to search..."
                      isDisabled={!selectedBusiness}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Payroll Month <span className="text-danger">*</span></Label>
                    <Select
                      value={selectedPayroll}
                      onChange={(selected) => setSelectedPayroll(selected)}
                      options={payrolls}
                      isClearable
                      placeholder="Select Payroll Month"
                      className={!selectedPayroll ? "is-invalid" : ""}
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <FormGroup className="mb-0 w-100">
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={handleApplyFilter}
                      disabled={!selectedPayroll}
                    >
                      Apply
                    </Button>
                    <Button
                      color="secondary"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>

            {/* Table Section */}
            <div className="mt-4">
              <h5>Payroll Details History ({filteredData.length} records)</h5>
              <div className="table-responsive">
                <Table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Payroll Month</th>
                      <th>Employee Code</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Absent Days</th>
                      <th>Gross Salary</th>
                      <th>Absent Deduction</th>
                      <th>Net Salary</th>
                      <th>Advance Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="11" className="text-center">Loading...</td>
                      </tr>
                    ) : currentRecords.length > 0 ? (
                      currentRecords.map((employee, index) => (
                        <tr key={employee.payrollDetail_Id || index}>
                          <td>{indexOfFirstRecord + index + 1}</td>
                          <td>{employee.payrollMonth}</td>
                          <td>{employee.employee_Code}</td>
                          <td>{employee.employee_Name}</td>
                          <td>{employee.designation_Name}</td>
                          <td>{employee.absent_Days || 0}</td>
                          <td>{(employee.gross_Salary || 0).toFixed(2)}</td>
                          <td>{(employee.absent_Deduction || 0).toFixed(2)}</td>
                          <td>{(employee.net_Salary || 0).toFixed(2)}</td>
                          <td>{(employee.salaryAdvance_Amount || 0).toFixed(2)}</td>
                          <td>
                            <Button
                              color="info"
                              size="sm"
                              className="me-1"
                              onClick={() => handleViewDetails(employee)}
                            >
                              <i className="fas fa-eye"></i> View
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handlePrintPayslip(employee)}
                            >
                              <i className="fas fa-print"></i> Print
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center">No records found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredData.length)} of {filteredData.length} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => paginate(i + 1)}>
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* View Modal */}
        <ViewModal
          isOpen={showViewModal}
          toggle={() => setShowViewModal(false)}
          employee={selectedViewEmployee}
          additionalComponents={additionalComponents}
          payrollOtherDetails={payrollOtherDetails}
        />

        {/* Payslip Modal */}
        <PayslipModal
          isOpen={showPayslipModal}
          toggle={() => setShowPayslipModal(false)}
          employee={selectedPayslipEmployee}
        />
      </Container>
    </div>
  );
};

export default PayrollDetailsHistoryReportPage; 